import React, {Component, createElement} from 'react'
import propTypes from 'prop-types'
import {get, isFunction, isDate} from 'lodash'
import Date from 'containers/date'
import {MinColumnSize} from './columnHeader'
import Gutter from './gutter'
import Hint from 'components/hint'
import {prefix} from 'helpers/component'
import {getIconUrl} from 'helpers/cdn'

const getClassName = prefix('pivotTable-row-')

class DefaultCell extends Component {
  getColumnDataValue = (columnData) => {
    if (isDate(columnData))
      return <Date>{columnData}</Date>

    return columnData
  }

  render = () => {
    const {column, entity, containerStyle, containerClassName, contentStyle, contentClassName} = this.props
    if (!isFunction(column.data))
      return 'column.data not specified'

    const columnData = column.data(entity)
    return (
      <span style={containerStyle} className={containerClassName}>
        <span style={contentStyle} className={contentClassName}>
          {this.getColumnDataValue(columnData)}
        </span>
      </span>
    )
  }
}

DefaultCell.propTypes = {
  column: propTypes.object.isRequired,
  entity: propTypes.object.isRequired,
  size: propTypes.number.isRequired,
  containerStyle: propTypes.object,
  containerClassName: propTypes.string,
  contentStyle: propTypes.object,
  contentClassName: propTypes.string,
}

const getCell = (entity, column, size, colIndex, rowIndex, rowStyle, isFixed, level, rowComponentProps) => {
  if (size == null) size = column.size
  if (size == null) size = 100
  if (size < MinColumnSize) size = MinColumnSize

  const cellStyle = column.style ? column.style(entity) : {}
  const contentClasses = ['cellContent']

  let containerClassName = getClassName('cellContainer')
  if (column.className) containerClassName += ' ' + column.className

  let contentStyle = {
    textAlign: column.align || 'left',
    ...rowStyle,
    ...cellStyle,
  }

  if (column.iconName) {
    let icon = column.iconName
    if (typeof icon === 'function') icon = icon(entity)
    if (icon) {
      const iconSrc = getIconUrl(icon)
      contentStyle = {...contentStyle, backgroundImage: `url(${iconSrc})`}
      contentClasses.push('cellContentWithIcon')
    }
  }

  const cell = column.cellClass || DefaultCell
  const cellProps = {
    key: column.key,
    size,
    column,
    entity,
    rowIndex,
    colIndex,
    level,
    isFixed,
    contentStyle,
    containerStyle: {width: size},
    containerClassName,
    contentClassName: getClassName(contentClasses),
    hideFixedRowAddButton: rowComponentProps.hideFixedRowAddButton,
    ...column.cellProps,
  }

  if (column.cellRef) cellProps.ref = (e) => column.cellRef(e, colIndex, rowIndex, level)
  return createElement(cell, cellProps)
}

const getControlCell = (entity, column, colIndex, rowIndex, rowStyle, isFixed, level, rowProps) => {
  if (column.onGetContent) {
    const cellProps = {
      key: column.key,
      column,
      entity,
      rowIndex,
      colIndex,
      level,
      isFixed,
      style: {width: column.size},
      className: getClassName('cellContainer'),
      rowProps,
    }
    return column.onGetContent(cellProps)
  }
}

class Row extends Component {

  state = {
    hintAnchor: null,
  }

  resolveOnGetShowRowHoverHint = () => {
    const {onGetShowRowHoverHint, entity} = this.props
    return onGetShowRowHoverHint ? onGetShowRowHoverHint(entity) : false
  }

  onMouseEnter = (e) => {
    if (this.resolveOnGetShowRowHoverHint()) {
      this.setState({hintAnchor: e.currentTarget})
    }
  }

  onMouseLeave = () => {
    if (this.resolveOnGetShowRowHoverHint()) {
      this.setState({hintAnchor: null})
    }
  }

  render() {
    const {
      entity,
      index,
      isLast,
      level,
      columns,
      hidden,
      style,
      columnSizes,
      controlColumns = [],
      actionColumns = [],
      showSubData,
      subDataProp,
      subDataExpanded,
      subDataHighlighted,
      isFixed = false,
      isSelected = false,
      isDragPreview,
      isDragPlaceholder,
      isDragging,
      innerRef,
      draggableProps,
      onSelect,
      onToggleSubData,
      onGetRowHoverHintMessage,
    } = this.props
    const {hintAnchor} = this.state

    let {highlight} = this.props
    if (highlight) {
      highlight = (
        <span
          className={getClassName('highlight')}
          style={{backgroundColor: highlight}}
        />
      )
    }

    const classes = ['root']
    if (index % 2 === 1) classes.push('root-alt')

    if (isDragPreview) classes.push('root-drag-preview')
    if (isDragPlaceholder) classes.push('root-drag-placeholder')
    if (isDragging) classes.push('root-dragging')
    if (isSelected) classes.push('root-selected')

    const controlWidth = controlColumns.reduce((total, col) => total += col.size, 0)
    const actionWidth = actionColumns.reduce((total, col) => total += col.size, 0)
    const columnStyle = controlWidth ? {width: `calc(100% - ${controlWidth + actionWidth}px)`} : null

    let gutter
    if (showSubData) {
      const hasSubData = (get(entity, subDataProp) || []).length > 0
      gutter = (
        <Gutter
          hasSubData={hasSubData}
          expanded={subDataExpanded}
          highlighted={subDataHighlighted}
          showLine={!isFixed}
          showTop={level > 0 || index > 0}
          showBottom={level > 0 || !isLast || subDataExpanded}
          shopTopPlate={subDataHighlighted && subDataExpanded && (index > 0 || level > 0)}
          showEndPlate={subDataHighlighted && !subDataExpanded && isLast}
          onToggle={onToggleSubData}
        />
      )
    }

    const hider = hidden ? <div className={getClassName('hider')}/> : null
    const highlighting = (subDataExpanded && subDataHighlighted)
    if (highlighting) classes.push('root-highlighted')
    if (hidden || highlighting) classes.push('root-hidden')

    //@Method getControlCell - @bool isFixed argument value is set in Grid component
    return (
      <div
        key="row"
        ref={innerRef}
        className={getClassName(classes)}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        {...draggableProps}
      >
        {this.resolveOnGetShowRowHoverHint() && (
          <Hint
            anchor={hintAnchor}
            visible={!!hintAnchor}
            label={onGetRowHoverHintMessage(entity)}
            small={true}
          />
        )}
        {gutter}
        {highlight}
        <div className={getClassName('controlColumnWrapper')}>
          {controlColumns.map((column, mapIndex) =>
            getControlCell(entity, column, mapIndex, index, style, isFixed, level, this.props))}
        </div>
        <div className={getClassName('columnWrapper')} style={columnStyle} onClick={onSelect}>
          {columns.map((column, mapIndex) =>
            getCell(
              entity,
              column,
              columnSizes[column.key],
              mapIndex,
              index,
              style,
              isFixed,
              level,
              this.props
            )
          )}
        </div>
        <div className={getClassName('actionColumnWrapper')}>
          {actionColumns.map((column, mapIndex) =>
            getControlCell(entity, column, mapIndex, index, style, isFixed, level, this.props))}
        </div>
        {hider}
      </div>
    )
  }
}

Row.propTypes = {
  entity: propTypes.object.isRequired,
  index: propTypes.number.isRequired, // index of this row within this grid/sub-grid
  isLast: propTypes.bool,             // true if this is the last row in this grid/sub-grid
  level: propTypes.number,
  highlight: propTypes.string,
  hidden: propTypes.bool,
  style: propTypes.object,
  columns: propTypes.arrayOf(propTypes.object).isRequired,
  columnSizes: propTypes.object.isRequired,
  controlColumns: propTypes.arrayOf(propTypes.shape({
    key: propTypes.any,
    size: propTypes.number.isRequired,
    onGetContent: propTypes.func,
  })),
  actionColumns: propTypes.arrayOf(propTypes.shape({
    key: propTypes.any,
    size: propTypes.number.isRequired,
    onGetContent: propTypes.func,
  })),
  showSubData: propTypes.bool,
  subDataProp: propTypes.string,
  isFixed: propTypes.bool,
  isSelected: propTypes.bool,
  isDragPreview: propTypes.bool,
  isDragPlaceholder: propTypes.bool,
  isDragging: propTypes.bool,
  innerRef: propTypes.oneOfType([propTypes.func, propTypes.object]),
  draggableProps: propTypes.object,
  dragHandleProps: propTypes.object,
  subDataExpanded: propTypes.bool,
  subDataHighlighted: propTypes.bool,
  onSelect: propTypes.func,
  onToggleSubData: propTypes.func,
  onGetShowRowHoverHint: propTypes.func,
  onGetRowHoverHintMessage: propTypes.func,
}

export default Row
